import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  useTheme,
  TextField
} from '@mui/material';

import Loader from 'src/common/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { addNewGroup, updateGroupDetails } from 'src/redux/groupSlice';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';

import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import InputField from 'src/common/formFields/InputField';
import { GROUP_TYPES } from 'src/utils/constants';
import { getGroupName } from 'src/utils/getInitials';
import {
  groupFormInitialValues,
  groupFormModel,
  groupValidationSchema
} from './groupFormModel';

const NewGroup = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { groupType } = useSelector((state) => state.groups);
  const [loading, setLoading] = useState(false);

  const formInitValues = initialValues || groupFormInitialValues;

  const {
    formId,
    formField: { grpType, groupName }
  } = groupFormModel;

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateGroupDetails({ groupType, values })).unwrap();
      } else {
        await dispatch(addNewGroup({ groupType, values })).unwrap();
      }

      handleClose();
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Group updated successfully'
            : 'New group created successfully'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'EDIT GROUP' : 'ADD NEW GROUP'}
        onCancel={handleClose}
      />

      <Formik
        initialValues={formInitValues}
        validationSchema={groupValidationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form id={formId}>
                <DialogContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name={grpType.name}
                        label={grpType.label}
                        value={getGroupName(groupType, GROUP_TYPES)}
                        disabled
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={groupName.name}
                        label={groupName.label}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <DialogActions
                  sx={{
                    p: 2.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: '#F9FAFB'
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    {initialValues ? 'Update Group' : 'Create Group'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewGroup.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewGroup;
